import React, { memo, useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import type { View as RNView } from 'react-native';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';

export interface DateTimePickerButtonProps {
  label: string;
  value: Date;
  onPress: (anchor?: AnchorRect) => void;
  error?: string;
  placeholder?: string;
}

export const DateTimePickerButton = memo<DateTimePickerButtonProps>(({
  label,
  value,
  onPress,
  error,
  placeholder = 'Select date and time',
}) => {
  const styles = useStyles();
  const { admin } = useAdminColors();
  const buttonRef = useRef<RNView | null>(null);

  const formatDateTime = (date: Date) => {
    return `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const handlePress = useCallback(() => {
    if (Platform.OS === 'web' && buttonRef.current && (buttonRef.current as any)?.measureInWindow) {
      (buttonRef.current as any).measureInWindow((x: number, y: number, w: number, h: number) => {
        onPress({ x, y, w, h });
      });
      return;
    }
    onPress();
  }, [onPress]);

  return (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity
        ref={buttonRef}
        style={[styles.dateTimeButton, error && styles.inputError]}
        onPress={handlePress}
      >
        <Text style={[styles.dateTimeText, !value && styles.placeholderText]}>
          {value ? formatDateTime(value) : placeholder}
        </Text>
        <SVG_COMPONENTS.CHEVRON_DOWN width={16} height={16} color={admin.dataTable.text} />
      </TouchableOpacity>
      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
});

DateTimePickerButton.displayName = 'DateTimePickerButton';

const useStyles = () =>
  useThemedStyles(({ components, semantic }) => {
    const admin = components.admin;
    return StyleSheet.create({
      inputGroup: {
        marginBottom: 16,
      },
      label: {
        fontSize: 14,
        fontWeight: '500',
        color: admin.formInput.label,
        marginBottom: 8,
      },
      dateTimeButton: {
        backgroundColor: admin.formInput.bg,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: admin.formInput.border,
        paddingHorizontal: 12,
        paddingVertical: 12,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: 48,
      },
      dateTimeText: {
        fontSize: 16,
        color: admin.formInput.text,
      },
      placeholderText: {
        color: admin.formInput.placeholder,
      },
      inputError: {
        borderColor: semantic.status.error,
      },
      errorText: {
        fontSize: 12,
        color: semantic.status.error,
        marginTop: 4,
      },
    });
  });
